/* Reveal-on-scroll using IntersectionObserver.
 *
 * Why not ScrollTrigger anymore: on mobile Safari, ScrollTrigger's start
 * positions are calculated against the visual viewport, which shifts as
 * the address bar collapses. IntersectionObserver is supported everywhere
 * we care about (Safari iOS 12.2+, Chrome 51+), needs zero scroll-event
 * polling, and is the right primitive for this exact task.
 *
 * Behavior:
 *   - .reveal elements fade + slide in once they cross the viewport edge.
 *   - One-shot by default (no leave / re-enter flicker on slow scrolls).
 *   - Honors prefers-reduced-motion.
 *   - Honors --reveal-delay (ms) for staggered groups.
 */

export function initRevealOnScroll() {
  const targets = document.querySelectorAll('.reveal');
  if (!targets.length) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    targets.forEach((el) => el.classList.add('is-in'));
    return;
  }

  // No-op fallback for browsers without IntersectionObserver: show everything.
  if (typeof IntersectionObserver === 'undefined') {
    targets.forEach((el) => el.classList.add('is-in'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = parseFloat(el.style.getPropertyValue('--reveal-delay')) || 0;
        if (delay > 0) {
          setTimeout(() => el.classList.add('is-in'), delay);
        } else {
          el.classList.add('is-in');
        }
        obs.unobserve(el);
      });
    },
    {
      // Fire while element is still ~12% below the fold so the animation
      // plays as it enters view, not after it's fully in.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.01,
    }
  );

  targets.forEach((el) => observer.observe(el));

  // Anything already above the fold at page load gets promoted immediately
  // (otherwise IO won't fire until the user scrolls).
  requestAnimationFrame(() => {
    targets.forEach((el) => {
      if (el.classList.contains('is-in')) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.95) {
        const delay = parseFloat(el.style.getPropertyValue('--reveal-delay')) || 0;
        if (delay > 0) {
          setTimeout(() => el.classList.add('is-in'), delay);
        } else {
          el.classList.add('is-in');
        }
        observer.unobserve(el);
      }
    });
  });
}
